import React from 'react';
import { AlertTriangle, CalendarOff, Moon, Sun } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const sectionName = (c) => c.section || 'No section';

const Metric = ({ label, value, tone, tv, testId }) => (
  <div>
    <div className={`${tv ? 'text-sm text-slate-400' : 'text-[11px] text-gray-500'} uppercase tracking-wide`}>{label}</div>
    <div className={`${tv ? 'text-3xl' : 'text-lg'} font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</div>
  </div>
);

/** One section: Present vs In Roll, waiting approval, absent (red when above the HR threshold). */
export const HeadcountCard = ({ c, tv, onOpen }) => {
  const pct = c.in_roll ? Math.round((c.present * 100) / c.in_roll) : 0;
  const base = tv ? 'bg-slate-800 text-white' : 'bg-white text-gray-900';
  const alertCls = tv ? 'border-red-500 bg-red-950/70' : 'border-red-500 bg-red-50';
  return (
    <button type="button" onClick={() => onOpen(c)} data-testid={`headcount-card-${c.key || 'none'}`}
      className={`text-left rounded-xl border-2 p-4 shadow-md transition-transform duration-150 hover:-translate-y-0.5 ${c.alert ? alertCls : `border-transparent ${base}`}`}>
      <div className="flex items-start gap-2">
        <div className={`flex-1 font-semibold truncate ${tv ? 'text-2xl' : 'text-base'}`} data-testid={`headcount-section-${c.key || 'none'}`}>{sectionName(c)}</div>
        {c.alert && <AlertTriangle className={`${tv ? 'w-7 h-7' : 'w-5 h-5'} text-red-500 shrink-0`} data-testid={`headcount-alert-${c.key || 'none'}`} />}
        {c.weekly_off && <span className="flex items-center gap-1 rounded-full bg-slate-200 text-slate-700 px-2 py-0.5 text-xs" data-testid={`headcount-weekoff-${c.key || 'none'}`}><CalendarOff className="w-3 h-3" />Weekly off</span>}
      </div>
      <div className={`mt-2 flex items-baseline gap-1 ${tv ? 'text-5xl' : 'text-3xl'} font-bold tabular-nums`}>
        <span className="text-emerald-500" data-testid={`headcount-present-${c.key || 'none'}`}>{c.present}</span>
        <span className={`${tv ? 'text-2xl text-slate-400' : 'text-base text-gray-400'}`}>/ <span data-testid={`headcount-inroll-${c.key || 'none'}`}>{c.in_roll}</span> present</span>
      </div>
      <div className={`mt-2 h-2 rounded-full ${tv ? 'bg-slate-700' : 'bg-gray-100'}`}><div className="h-2 rounded-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
      <div className={`mt-2 flex items-center gap-3 ${tv ? 'text-lg' : 'text-sm'} font-semibold`}>
        <span className="flex items-center gap-1 text-amber-500" data-testid={`headcount-day-${c.key || 'none'}`}><Sun className={tv ? 'w-5 h-5' : 'w-4 h-4'} />Day {c.day_shift}</span>
        <span className={`flex items-center gap-1 ${tv ? 'text-indigo-300' : 'text-indigo-600'}`} data-testid={`headcount-night-${c.key || 'none'}`}><Moon className={tv ? 'w-5 h-5' : 'w-4 h-4'} />Night {c.night_shift}</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Metric tv={tv} label="Waiting approval" value={c.pending} tone="text-amber-500" testId={`headcount-pending-${c.key || 'none'}`} />
        <Metric tv={tv} label={`Absent · ${c.absent_pct}%`} value={c.absent} tone={c.alert ? 'text-red-500' : tv ? 'text-slate-200' : 'text-gray-700'} testId={`headcount-absent-${c.key || 'none'}`} />
        <Metric tv={tv} label="Late" value={c.late} tone="text-orange-500" testId={`headcount-late-${c.key || 'none'}`} />
      </div>
    </button>
  );
};

const REASON = { no_punch: 'No punch yet', rejected: 'Punch rejected' };
const TYPE = { regular: 'Regular', contract: 'Contract' };

export const AbsenteeDialog = ({ card, onClose }) => (
  <Dialog open={!!card} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg" data-testid="absentee-dialog">
      <DialogHeader>
        <DialogTitle>{card && sectionName(card)} — absent ({card?.absent})</DialogTitle>
        <DialogDescription>In Roll staff with no check-in (or a rejected one) for this day.</DialogDescription>
      </DialogHeader>
      <div className="max-h-[60vh] overflow-y-auto divide-y">
        {card?.absentees.length === 0 && <p className="py-6 text-center text-gray-500" data-testid="absentee-none">Everyone is present.</p>}
        {card?.absentees.map((a) => (
          <div key={a.staff_id} className="flex items-center gap-3 py-2" data-testid={`absentee-row-${a.staff_id}`}>
            <span className="font-mono text-sm text-gray-500 w-24 shrink-0">{a.staff_id}</span>
            <span className="flex-1 font-medium">{a.staff_name}</span>
            {a.worker_type && <span className="text-xs text-gray-500">{TYPE[a.worker_type]}</span>}
            <span className="text-xs text-gray-500" data-testid={`absentee-collar-${a.staff_id}`}>{a.collar ? `${a.collar === 'white' ? 'White' : 'Blue'} collar` : 'Collar not set'}</span>
            <span className={`text-xs rounded-full px-2 py-0.5 ${a.reason === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{REASON[a.reason]}</span>
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);
